import { Modal } from "@mui/material";
import { RotateCcw, X, Settings, ChevronDown, AlertTriangle, Search } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { showError } from "../../utils/toast";

interface RescanData {
    agent_id: string;
    job_type: string;
    parameters: any;
    client_id: string;
}

interface RescanModalProps {
    open: boolean;
    onClose: () => void;
    agent: any;
    onSubmit: (data: RescanData) => void;
    customJobTypes?: { value: string; label: string; }[];
}

const JOB_TYPES = [
    { value: 'camera_scan', label: 'Camera Discovery' },
    { value: 'nvr', label: 'NVR' },

];

const RescanModal: React.FC<RescanModalProps> = ({ open, onClose, agent, onSubmit, customJobTypes }) => {
    const displayJobTypes = customJobTypes || JOB_TYPES;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedJobType, setSelectedJobType] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Update dropdown position when it opens
    useEffect(() => {
        if (showDropdown && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width
            });
        }
    }, [showDropdown]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const selectJobType = (jobType: string) => {
        setSelectedJobType(jobType);
        setShowDropdown(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedJobType) {
            showError('Please select a job type');
            return;
        }

        setIsSubmitting(true);

        try {
            const rescanData: RescanData = {
                client_id: agent?.client_id,
                agent_id: agent?.id,
                job_type: selectedJobType,
                parameters: {}, // Parameters are now empty as per your request to remove the form
            };

            onSubmit(rescanData);
            handleClose();
        } catch (error) {
            console.error('Rescan failed:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setSelectedJobType('');
        setShowDropdown(false);
        setIsSubmitting(false);
        onClose();
    };

    const getStatusBg = (status: string) => {
        switch (status) {
            case 'online': return 'bg-green-400';
            case 'offline': return 'bg-gray-400';
            case 'busy': return 'bg-yellow-400';
            case 'error': return 'bg-red-400';
            default: return 'bg-gray-400';
        }
    };

    const selectedJob = displayJobTypes.find(job => job.value === selectedJobType);

    return (
        <Modal open={open} onClose={handleClose}>
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                w-[95%] sm:w-[450px] max-w-md bg-white 
                border border-slate-200 shadow-2xl rounded-2xl text-slate-800
                relative z-[9997] overflow-hidden">

                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#422568] via-[#422568] to-[#422568]"></div>

                <form onSubmit={handleSubmit} className="p-8">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center">
                            <div className="w-10 h-10 bg-[#422568] rounded-xl flex items-center justify-center mr-3 text-white">
                                <RotateCcw
                                    className={`h-6 w-6 ${isSubmitting ? 'animate-spin' : ''}`}
                                />
                            </div>
                            <h2 className="text-xl font-extrabold text-slate-800 tracking-tight">
                                {agent?.last_job_completed_at ? 'Rescan Agent' : 'Scan Agent'}
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="text-slate-400 hover:text-slate-600 transition-colors p-2 rounded-lg hover:bg-slate-100"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="space-y-6">
                        {/* Agent Info */}
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className={`w-3 h-3 ${getStatusBg(agent?.status || 'offline')} rounded-full shadow-lg shadow-green-500/20`}></div>
                                <div>
                                    <p className="text-slate-800 font-extrabold text-sm">{agent?.name}</p>
                                    <p className="text-slate-500 text-xs font-mono font-bold tracking-tight">{agent?.ip_address}</p>
                                </div>
                            </div>
                        </div>

                        {/* Job Type Selection */}
                        <div className="relative">
                            <label className="flex items-center text-[11px] font-extrabold text-slate-500 mb-2 uppercase tracking-widest">
                                <Settings className="w-4 h-4 mr-2 text-[#422568]" />
                                Select Scan Type <span className='text-red-500 ml-1'>*</span>
                            </label>

                            <div className="relative" ref={dropdownRef}>
                                <button
                                    ref={buttonRef}
                                    type="button"
                                    onClick={() => setShowDropdown(!showDropdown)}
                                    className={`w-full bg-white border ${!selectedJobType ? 'border-slate-200' : 'border-[#422568]'} 
                                        rounded-xl px-4 py-3 text-slate-800 text-left shadow-sm
                                        focus:outline-none focus:ring-2 focus:ring-[#422568]/20 
                                        transition-all duration-200 flex items-center justify-between group`}
                                >
                                    <span className={!selectedJobType ? 'text-slate-400' : 'text-slate-800 font-bold'}>
                                        {selectedJob ? selectedJob.label : 'Choose a scan job...'}
                                    </span>
                                    <ChevronDown
                                        className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${showDropdown ? 'rotate-180 text-[#422568]' : 'group-hover:text-slate-800'}`}
                                    />
                                </button>

                                {showDropdown && createPortal(
                                    <>
                                        <div className="fixed inset-0 z-[99998]" onClick={() => setShowDropdown(false)} />
                                        <div
                                            className="fixed bg-white border border-slate-200 
                                                rounded-xl shadow-2xl z-[99999] max-h-60 overflow-y-auto
                                                scrollbar-thin scrollbar-thumb-slate-200"
                                            style={{
                                                top: `${dropdownPosition.top}px`,
                                                left: `${dropdownPosition.left}px`,
                                                width: `${dropdownPosition.width}px`
                                            }}
                                        >
                                            {displayJobTypes.map((jobType, index) => (
                                                <button
                                                    key={jobType.value}
                                                    type="button"
                                                    onClick={() => selectJobType(jobType.value)}
                                                    className={`w-full text-left px-4 py-3 hover:bg-[#422568] hover:text-white transition-colors
                                                        ${index < displayJobTypes.length - 1 ? 'border-b border-slate-100' : ''} 
                                                        ${selectedJobType === jobType.value ? 'bg-[#422568] text-white font-extrabold' : 'text-slate-700 font-bold'}
                                                        first:rounded-t-xl last:rounded-b-xl text-sm`}
                                                >
                                                    {jobType.label}
                                                </button>
                                            ))}
                                        </div>
                                    </>,
                                    document.body
                                )}
                            </div>

                            {!selectedJobType && (
                                <p className="mt-2 text-[11px] text-slate-400 flex items-center italic font-bold">
                                    <AlertTriangle className="w-3 h-3 mr-1 text-[#422568]/50" />
                                    Pick a scan type to initiate the agent process
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex justify-end gap-3 mt-10 pt-6 border-t border-slate-100">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-6 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-800 transition-colors rounded-xl hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || !selectedJobType}
                            className="flex items-center gap-2 px-8 py-2.5 bg-[#422568] 
                                text-white rounded-xl font-extrabold hover:bg-[#422568] 
                                shadow-lg shadow-[#422568] transition-all duration-300 
                                disabled:opacity-30 disabled:cursor-not-allowed min-w-[140px] justify-center text-sm"
                        >
                            {isSubmitting ? (
                                <>
                                    <RotateCcw className="w-4 h-4 animate-spin" />
                                    Starting...
                                </>
                            ) : (
                                <>
                                    <Search className="w-4 h-4" />
                                    Start Scan
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default RescanModal;
